import { FC, useMemo } from "react";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CheckIcon } from "@icons";
import { pluralize } from "@common/helpers/string";
import styles from "./AddClientDrawerSteps.module.scss";

interface Props {
    clientName: string;
    entityCount: number;
    planLabel: string;
    onClose: () => void;
    onAddAnotherClient: () => void;
    isAddEntityOnlyFlow: boolean;
    billingTransferEnabled: boolean;
    invitedCount: number;
}

export const ResultStep: FC<Props> = ({
    clientName,
    entityCount,
    planLabel,
    onClose,
    onAddAnotherClient,
    isAddEntityOnlyFlow,
    billingTransferEnabled,
    invitedCount,
}) => {
    const entityWord = pluralize("entity", entityCount);

    const title = isAddEntityOnlyFlow
        ? "All set up"
        : `${clientName} is all set up`;

    const entitySummaryText = isAddEntityOnlyFlow
        ? `${entityCount} ${entityWord} added`
        : `${entityCount} ${entityWord} on the ${planLabel} plan`;

    const subtitle = useMemo(() => {
        if (billingTransferEnabled) {
            return "Your client workspace is ready. We invited the client to set up their account and add a billing method.";
        }
        if (invitedCount > 0) {
            return "Your client workspace is ready. We invited the client to set up their account.";
        }
        return "Your client workspace is ready. You can invite people anytime.";
    }, [billingTransferEnabled, invitedCount]);

    const invitedNumber = invitedCount + (billingTransferEnabled ? 1 : 0);

    return (
        <div className={styles.resultContainer}>
            <div className={styles.iconCircle}>
                <CheckIcon />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.subtitle}>{subtitle}</div>
            <div className={styles.summaryPills}>
                <div className={styles.summaryPill}>
                    <CheckIcon />
                    <span>{entitySummaryText}</span>
                </div>
                {invitedNumber > 0 && (
                    <div className={styles.summaryPill}>
                        <CheckIcon />
                        <span>
                            {invitedNumber}{" "}
                            {invitedNumber === 1 ? "person" : "people"} invited
                        </span>
                    </div>
                )}
            </div>
            <div className={styles.actions}>
                {!isAddEntityOnlyFlow && (
                    <Button
                        variant="primary"
                        type="button"
                        size="xl"
                        onClick={onAddAnotherClient}
                    >
                        Add another client
                    </Button>
                )}
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Done",
                        enabled: true,
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            type="button"
                            size="xl"
                            onClick={onClose}
                            rightIcon={hotkeyHint}
                        >
                            Done
                        </Button>
                    )}
                </WithHotkey>
            </div>
        </div>
    );
};
